import {
  DisplayCategoryKey,
  ExtensionKey,
  JobTypeKey,
  ProductWeights,
  buildFilters,
  type Filter,
  type FilterOption,
  type ProductType,
  type PromptTabs,
} from "@1js/pl-card-components";
import {
  isUserPrompt,
  type Group,
  type MicrosoftPrompt,
  type Prompt,
  type Status,
  type UserPrompt,
  type WorkgroupMicrosoftPrompt,
  type WorkgroupUserPrompt,
} from "@1js/pl-types";
import { Avatar } from "@fluentui/react-components";
import React, { useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { deserializeFilters } from "../../../components/AllPrompts/AllPrompts.web.data";
import type { WebPrompt } from "../../../components/data";
import type { PromptReference } from "../../../services/prompt-client.web";
import { sendClientLayoutEvent } from "../../../utils/Telemetry";

const pageSize = 16;

export type PromptLibraryState = {
  allPrompts: Prompt[];
  allFilteredPrompts: Prompt[];
  displayedPrompts: Prompt[];
  moreLikeThis: WebPrompt[];
  selectedFacets: Record<string, string[]>;
  selectedWorkgroupFacets: Record<string, string[]>;
  initSearchParams: URLSearchParams;
  filters: Filter[];
  workgroupFilters: Filter[];
  status: Status;
  hasMore?: boolean;
  selectedTab: PromptTabs;
  hasBookmarkedPrompts?: boolean;
  groups?: Group[];
  groupPromptStatus: Status;
  groupPrompts: Record<
    string,
    (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
  >;
  displayedGroupPrompts: Prompt[];

  groupHasPromptsStatus: Status;
  groupMemberStatus: Status;
  groupsHasPrompts?: Group[];
  groupsHasPromptsInit?: boolean;
  groupsMember?: Group[];
  groupsMemberInit?: boolean;
  serviceHost: string;
  locale: string;

  extensionIconMap?: Record<string, string>;
};

export const initialState: PromptLibraryState = {
  allPrompts: [],
  allFilteredPrompts: [],
  displayedPrompts: [],
  moreLikeThis: [],
  selectedFacets: {
    displayCategory: [],
    "products/name": [],
    jobType: [],
    createdBy: ["CreatedByAll"],
    extension: [],
  },
  selectedWorkgroupFacets: {
    workgroup: [],
  },
  status: "loading",
  filters: [],
  workgroupFilters: [],
  selectedTab: "AllPrompts",
  groups: undefined,
  groupPrompts: {},
  groupPromptStatus: "idle",
  displayedGroupPrompts: [],
  initSearchParams: new URLSearchParams(),
  groupHasPromptsStatus: "idle",
  groupMemberStatus: "idle",
  groupsHasPrompts: undefined,
  groupsHasPromptsInit: false,
  groupsMember: undefined,
  groupsMemberInit: false,
  serviceHost: "https://copilot.cloud.microsoft/",
  locale: "",
  extensionIconMap: {},
};

export type PromptLibraryAction =
  | {
      type: "SET_PROMPTS";
      payload: {
        prompts: Prompt[];
        extensionIconMap?: Record<string, string>;
        status: Status;
      };
    }
  | {
      type: "SET_GROUP_PROMPTS";
      payload: {
        groupId: string;
        groupPrompts: (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[];
      };
    }
  | {
      type: "INVALIDATE_GROUP_PROMPTS";
      payload: { groupIds: string[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_SUCCESS";
      payload: {
        groupIds: string[];
        promptsToBeDeleted: Prompt[];
        deletedPrompts: PromptReference[];
      };
    }
  | {
      type: "PUBLISH_GROUP_PROMPT_SUCCESS";
      payload: {
        requestedGroupIds: string[];
        requestedPrompts: Prompt[];
        publishedPromptIds: PromptReference[];
        displayName: string;
        email?: string;
      };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_INIT";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_LONG";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_FAILED";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "SET_SELECTED_FACETS";
      payload: {
        facets: Record<string, string[]>;
        isAgentPromptSavingEnabled?: boolean;
      };
    }
  | {
      type: "LOAD_MORE";
    }
  | {
      type: "BOOKMARK_INIT";
      payload: { prompt: Prompt };
    }
  | {
      type: "BOOKMARK_SUCCEED";
      payload: { prompt: Prompt; value: boolean };
    }
  | {
      type: "BOOKMARK_FAILED";
      payload: { prompt: Prompt };
    }
  | {
      type: "MORE_LIKE_THIS";
      payload: { prompt: WebPrompt };
    }
  | {
      type: "TOGGLE_BOOKMARK_ONLY";
    }
  | {
      type: "SET_BOOKMARK_ONLY";
      payload: { selectedTab: PromptTabs };
    }
  | {
      type: "SET_GROUP_PROMPTS_SELECTED_FACETS_SUCCESS";
      payload: { facets: Record<string, string[]> };
    }
  | {
      type: "JUMP_TO_GROUP_PROMPTS_GROUP";
      payload: { groupId: string };
    }
  | {
      type: "SET_GROUP_PROMPTS_SELECTED_FACETS_FAILED";
      payload: { facets: Record<string, string[]> };
    }
  | {
      type: "SET_GROUP_PROMPTS_SELECTED_FACETS_INIT";
      payload: { facets: Record<string, string[]> };
    }
  | {
      type: "DELETE_UNDO";
      payload: { prompt: Prompt; promptPosition: number };
    }
  | {
      type: "DELETE_SUCCEED";
      payload: { prompt: Prompt };
    }
  | {
      type: "REQUEST_ALL_GROUPS_MEMBER_INIT";
    }
  | {
      type: "REQUEST_ALL_GROUPS_MEMBER_SUCCEED";
      payload: { groups: Group[]; groupId?: string };
    }
  | {
      type: "REQUEST_ALL_GROUPS_MEMBER_FAILED";
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_INIT";
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED";
      payload: { groups: Group[]; groupId?: string };
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED";
    };

const filterGroupPrompts = (
  groupPrompts: Record<string, Prompt[]> | undefined,
  facets: Record<string, string[]>
) => {
  if (!groupPrompts) {
    return [];
  }

  let prompts: Prompt[] = [];
  for (const selectedWorkgroup of facets.workgroup) {
    if (groupPrompts[selectedWorkgroup]) {
      prompts = prompts.concat(groupPrompts[selectedWorkgroup]);
    }
  }

  return prompts;
};

const filterPrompts = (
  prompts: Prompt[],
  filters: Record<string, string[]>,
  favoritesOnly: boolean,
  isAgentPromptSavingEnabled?: boolean
) => {
  // if show favorite only, we want to show all Microsoft prompts that's favorited and all user-authored prompts.
  let filteredPrompts = favoritesOnly
    ? prompts.filter(
        (p) => p.UserActivity.Favorite.IsFavorite || p.Origin === "User"
      )
    : prompts.filter((p) => p.Origin !== "User");

  // Loop through each key-value pair in the filters object
  for (const [key, values] of Object.entries(filters)) {
    switch (key) {
      case DisplayCategoryKey:
        filteredPrompts =
          values.length > 0
            ? filteredPrompts.filter((f) => values.includes(f.DisplayCategory))
            : filteredPrompts;
        break;
      case "products/name":
        filteredPrompts =
          values.length > 0
            ? filteredPrompts.filter((f) =>
                values.some((item) => f.Products.includes(item))
              )
            : filteredPrompts;
        break;
      case JobTypeKey:
        filteredPrompts =
          values.length > 0
            ? filteredPrompts.filter((f) =>
                values.some((item) =>
                  f.Metadata?.find((m) => {
                    return m.Value === item;
                  })
                )
              )
            : filteredPrompts;
        break;
      case ExtensionKey:
        filteredPrompts =
          values.length > 0 &&
          (isAgentPromptSavingEnabled ? true : !favoritesOnly)
            ? filteredPrompts.filter((f) =>
                values.some((item) => f.ProviderName === item)
              )
            : filteredPrompts;
        break;
      case "createdBy":
        if (favoritesOnly && values.length > 0) {
          const createdByValue = values[0];
          if (createdByValue === "CreatedByMe") {
            filteredPrompts = filteredPrompts.filter(
              (p) => p.Origin === "User"
            );
          } else if (createdByValue === "CreatedByMicrosoft") {
            filteredPrompts = filteredPrompts.filter(
              (p) => p.Origin !== "User"
            );
          }
        }
        break;
    }
  }
  return filteredPrompts;
};
/**
 * Sort product type filters by weight
 * @param filters
 */
const sortProductTypeFilters = (filters: Filter[]): void => {
  filters.forEach((filter) => {
    if (filter.key === "products/name") {
      filter.options = filter.options.sort((a, b) => {
        const aWeight = ProductWeights[a.value as ProductType] || 0;
        const bWeight = ProductWeights[b.value as ProductType] || 0;
        return aWeight - bWeight;
      });
    }
  });
};

/**
 * Get similar prompts from the same workgroup
 * @param groupPrompts - all prompts in the same workgroup
 * @param prompt - the prompt to find similar prompts for
 * @returns - similar prompts from the same workgroup
 */
const getMoreLikeThisFromTheSameGroup = (
  groupPrompts: Prompt[] | undefined,
  prompt: Prompt
): WebPrompt[] => {
  return groupPrompts
    ?.filter((x) => x.Id !== prompt.Id)
    .slice(0, 2) as WebPrompt[];
};

/**
 * Get similar prompts from the same display category
 * @param allPrompts - all prompts
 * @param prompt - the prompt to find similar prompts for
 * @returns - similar prompts from the same display category
 */
const getMoreLikeThisFromTheSameCategory = (
  allPrompts: Prompt[],
  prompt: Prompt
): WebPrompt[] => {
  return allPrompts
    .filter(
      (x) => x.DisplayCategory === prompt.DisplayCategory && x.Id !== prompt.Id
    )
    .slice(0, 2) as WebPrompt[];
};

const reducer = (
  state: PromptLibraryState,
  action: PromptLibraryAction
): PromptLibraryState => {
  const createWorkgroupFilter = (groups: Group[]): Filter[] => {
    const options: FilterOption[] = [];
    groups
      ?.filter((group) => group.HasPrompts && group.IsMember)
      .map((group) => {
        const image = group.AvatarUrl ? { src: group.AvatarUrl } : undefined;

        const filterOption: FilterOption = {
          value: group.Id,
          displayLabel: group.DisplayName,
          badge: (
            <Avatar
              name={group.DisplayName}
              shape="square"
              image={image}
              color="colorful"
              size={20}
            />
          ),
        };
        options.push(filterOption);
      });

    const workgroupFilters: Filter[] = [
      {
        key: "workgroup",
        options,
        type: "combobox",
        isSingleSelect: true,
        isOverflowItemsFrom10: true,
        isLogValuesInTelemetry: false,
      },
    ];

    return workgroupFilters;
  };

  switch (action.type) {
    case "SET_GROUP_PROMPTS": {
      const groupPrompts: Record<
        string,
        (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
      > = { ...state.groupPrompts };
      groupPrompts[action.payload.groupId] = action.payload.groupPrompts;

      return {
        ...state,
        groupPromptStatus: "fulfilled",
        groupPrompts,
        displayedGroupPrompts: filterGroupPrompts(
          groupPrompts,
          state.selectedWorkgroupFacets
        ),
      };
    }
    case "INVALIDATE_GROUP_PROMPTS": {
      const groupPrompts: Record<
        string,
        (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
      > = { ...state.groupPrompts };
      action.payload.groupIds.forEach((groupId) => {
        delete groupPrompts[groupId];
      });
      return {
        ...state,
        groupPrompts,
      };
    }
    case "REMOVE_GROUP_PROMPT_SUCCESS": {
      action.payload.promptsToBeDeleted.forEach((prompt) => {
        prompt.RemovingState = "false";
      });

      const groupPrompts: Record<
        string,
        (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
      > = { ...state.groupPrompts };
      const deletedPromptSet = new Set(
        action.payload.deletedPrompts.map((prompt) => prompt.PromptId)
      );
      action.payload.groupIds.forEach((groupId) => {
        groupPrompts[groupId] = groupPrompts[groupId]?.filter(
          (prompt) => !deletedPromptSet.has(prompt.PromptId)
        );
      });
      return {
        ...state,
        groupPrompts,
        displayedGroupPrompts: filterGroupPrompts(
          groupPrompts,
          state.selectedWorkgroupFacets
        ),
      };
    }
    case "PUBLISH_GROUP_PROMPT_SUCCESS": {
      const groupPrompts: Record<
        string,
        (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
      > = { ...state.groupPrompts };
      const newGroups = state.groups ? [...state.groups] : [];
      const newGroupsMember = state.groupsMember ? [...state.groupsMember] : [];
      let promptIndex = 0;
      const groupHasPrompts =
        (state.groupsHasPrompts && [...state.groupsHasPrompts]) || [];
      action.payload.requestedPrompts.forEach((prompt) => {
        action.payload.requestedGroupIds.forEach((groupId) => {
          const sharedPrompt: MicrosoftPrompt | UserPrompt = JSON.parse(
            JSON.stringify(prompt)
          );
          sharedPrompt.Groups = sharedPrompt.Groups || [];
          const group = state.groups?.find((group) => group.Id === groupId);
          if (group) {
            // Mark the prompt shared with the group
            sharedPrompt.Groups.push(group);

            // mark the group as having prompts, so it lights up in the UX
            if (!group.HasPrompts) {
              groupHasPrompts?.push(group);
              group.HasPrompts = true;
            }
          }

          // If we fetched the group then fill the prompt in.
          // If we did not fetch the group yet, it will be fetched when accessed
          if (groupPrompts[groupId]) {
            const id = action.payload.publishedPromptIds[promptIndex].PromptId;
            const promptId = id.split("_")[0];
            groupPrompts[groupId].splice(0, 0, {
              ...sharedPrompt,
              Id: id,
              PromptId: promptId,
              Publisher: {
                Name: action.payload.displayName,
                Address: action.payload.email,
              },
            });
          }
          promptIndex++;
        });
      });
      return {
        ...state,
        groupPrompts,
        displayedGroupPrompts: filterGroupPrompts(
          groupPrompts,
          state.selectedWorkgroupFacets
        ),
        groupsHasPrompts: groupHasPrompts,
        groups: newGroups,
        groupsMember: newGroupsMember,
        workgroupFilters: createWorkgroupFilter(newGroups),
      };
    }
    case "REMOVE_GROUP_PROMPT_INIT": {
      action.payload.prompts.forEach((prompt) => {
        prompt.RemovingState = "true";
      });

      return {
        ...state,
      };
    }
    case "REMOVE_GROUP_PROMPT_LONG": {
      action.payload.prompts.forEach((prompt) => {
        if (prompt.RemovingState === "true") {
          prompt.RemovingState = "long";
        }
      });

      return {
        ...state,
      };
    }
    case "REMOVE_GROUP_PROMPT_FAILED": {
      action.payload.prompts.forEach((prompt) => {
        prompt.RemovingState = "false";
      });

      return {
        ...state,
      };
    }
    case "SET_PROMPTS": {
      // we always want to display the user-authored prompts before Microsoft created ones
      const userAuthoredPrompts = action.payload.prompts.filter(isUserPrompt);
      const nonUserAuthoredPrompts = action.payload.prompts.filter(
        (prompt) => !isUserPrompt(prompt)
      );
      const allPrompts = [...userAuthoredPrompts, ...nonUserAuthoredPrompts];

      const filteredPrompts = filterPrompts(
        allPrompts,
        state.selectedFacets,
        state.selectedTab === "SavedPrompts"
      );
      const displayedPrompts = filteredPrompts.slice(0, pageSize);

      sendClientLayoutEvent([...displayedPrompts], "Vertical");

      // I can't put createdByFilter inside the buildFilters helper function yet because WEPO endpoints haven't confirm
      // that they want the bookmark tab yet which is where this filter would show up. So if I put it inside buildFilters,
      // it would surface createdBy filter and that's not a good surprise.
      const createdByFilter: Filter = {
        key: "createdBy",
        isSingleSelect: true,
        options: [
          { value: "CreatedByAll" },
          { value: "CreatedByMe" },
          { value: "CreatedByMicrosoft" },
        ],
        type: "dropdown",
        isLogValuesInTelemetry: true,
      };

      const filters: Filter[] = [createdByFilter, ...buildFilters(allPrompts)];
      sortProductTypeFilters(filters);

      return {
        ...state,
        allPrompts: [...allPrompts],
        allFilteredPrompts: [...filteredPrompts],
        displayedPrompts,
        filters: filters,
        selectedFacets: deserializeFilters(state.initSearchParams, filters),
        hasMore: allPrompts.length > pageSize,
        status: action.payload.status,
        hasBookmarkedPrompts: allPrompts.some(
          (x) => x.UserActivity.Favorite.IsFavorite || x.Origin === "User"
        ),
        extensionIconMap: action.payload.extensionIconMap ?? {},
      };
    }
    case "SET_SELECTED_FACETS": {
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        action.payload.facets,
        state.selectedTab === "SavedPrompts",
        action.payload.isAgentPromptSavingEnabled
      );
      return {
        ...state,
        selectedFacets: action.payload.facets,
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: filteredPrompts.slice(0, 16),
        hasMore: filteredPrompts.length > pageSize,
      };
    }
    case "LOAD_MORE": {
      const newDisplayedPrompts = state.allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length + pageSize
      );

      sendClientLayoutEvent([...newDisplayedPrompts], "Vertical");

      return {
        ...state,
        displayedPrompts: newDisplayedPrompts,
        hasMore: newDisplayedPrompts.length < state.allFilteredPrompts.length,
      };
    }
    case "BOOKMARK_INIT":
      action.payload.prompt.IsBookmarking = true;
      return {
        ...state,
        allPrompts: [...state.allPrompts],
        displayedPrompts: [...state.displayedPrompts],
      };
    case "BOOKMARK_SUCCEED": {
      action.payload.prompt.IsBookmarking = false;
      action.payload.prompt.UserActivity.Favorite.IsFavorite =
        action.payload.value;
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        state.selectedFacets,
        state.selectedTab === "SavedPrompts"
      );
      return {
        ...state,
        allPrompts: [...state.allPrompts],
        allFilteredPrompts: filteredPrompts,
        displayedPrompts:
          state.selectedTab === "SavedPrompts"
            ? state.displayedPrompts.filter(
                (prompt) =>
                  prompt.UserActivity.Favorite.IsFavorite ||
                  isUserPrompt(prompt)
              )
            : [...state.displayedPrompts],
        hasBookmarkedPrompts: state.allPrompts.some(
          (prompt) =>
            prompt.UserActivity.Favorite.IsFavorite || isUserPrompt(prompt)
        ),
      };
    }
    case "BOOKMARK_FAILED":
      action.payload.prompt.IsBookmarking = false;
      return {
        ...state,
        allPrompts: [...state.allPrompts],
        displayedPrompts: [...state.displayedPrompts],
      };
    case "MORE_LIKE_THIS": {
      const groupPrompts = filterGroupPrompts(
        state.groupPrompts,
        state.selectedWorkgroupFacets
      );
      const moreLikeThis = action.payload.prompt.Groups
        ? getMoreLikeThisFromTheSameGroup(groupPrompts, action.payload.prompt)
        : getMoreLikeThisFromTheSameCategory(
            state.allPrompts,
            action.payload.prompt
          );
      return {
        ...state,
        moreLikeThis: moreLikeThis ?? [],
      };
    }
    case "TOGGLE_BOOKMARK_ONLY": {
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        state.selectedFacets,
        state.selectedTab !== "SavedPrompts"
      );
      return {
        ...state,
        selectedTab:
          state.selectedTab === "SavedPrompts" ? "AllPrompts" : "SavedPrompts",
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: filteredPrompts.slice(0, 16),
        hasMore: filteredPrompts.length > pageSize,
      };
    }
    case "SET_BOOKMARK_ONLY": {
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        state.selectedFacets,
        action.payload.selectedTab === "SavedPrompts"
      );
      return {
        ...state,
        selectedTab: action.payload.selectedTab,
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: filteredPrompts.slice(0, 16),
        hasMore: filteredPrompts.length > pageSize,
        displayedGroupPrompts: filterGroupPrompts(
          state.groupPrompts,
          state.selectedWorkgroupFacets
        ),
      };
    }
    case "SET_GROUP_PROMPTS_SELECTED_FACETS_INIT": {
      return {
        ...state,
        selectedWorkgroupFacets: action.payload.facets,
        groupPromptStatus: "loading",
        displayedGroupPrompts: filterGroupPrompts(
          state.groupPrompts,
          action.payload.facets
        ),
      };
    }
    case "SET_GROUP_PROMPTS_SELECTED_FACETS_SUCCESS": {
      return {
        ...state,
        selectedWorkgroupFacets: action.payload.facets,
        displayedGroupPrompts: filterGroupPrompts(
          state.groupPrompts,
          action.payload.facets
        ),
        groupPromptStatus: "fulfilled",
      };
    }
    case "JUMP_TO_GROUP_PROMPTS_GROUP": {
      return {
        ...state,
        selectedWorkgroupFacets: { workgroup: [action.payload.groupId] },
        selectedTab: "GroupPrompts",
        displayedGroupPrompts: filterGroupPrompts(state.groupPrompts, {
          workgroup: [action.payload.groupId],
        }),
      };
    }
    case "SET_GROUP_PROMPTS_SELECTED_FACETS_FAILED": {
      return {
        ...state,
        selectedWorkgroupFacets: action.payload.facets,
        displayedGroupPrompts: [],
        groupPromptStatus: "failed",
      };
    }
    case "DELETE_UNDO": {
      const allPrompts = [...state.allPrompts];

      // make sure the prompt is not already in the list
      if (
        allPrompts.findIndex(
          (prompt) => prompt.Id === action.payload.prompt.Id
        ) === -1
      ) {
        allPrompts.splice(
          action.payload.promptPosition,
          0,
          action.payload.prompt
        );
      }

      const allFilteredPrompts = filterPrompts(
        allPrompts,
        state.selectedFacets,
        state.selectedTab === "SavedPrompts"
      );
      const displayedPrompts = allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length + 1
      );

      return {
        ...state,
        allPrompts,
        allFilteredPrompts,
        displayedPrompts,
        hasMore: allFilteredPrompts.length > pageSize,
      };
    }
    case "DELETE_SUCCEED": {
      const allPrompts = [...state.allPrompts].filter(
        (prompt) => prompt.Id !== action.payload.prompt.Id
      );
      const allFilteredPrompts = filterPrompts(
        allPrompts,
        state.selectedFacets,
        state.selectedTab === "SavedPrompts"
      );
      const displayedPrompts = allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length
      );

      return {
        ...state,
        allPrompts,
        allFilteredPrompts,
        displayedPrompts,
        hasMore: allFilteredPrompts.length > pageSize,
      };
    }
    case "REQUEST_ALL_GROUPS_MEMBER_INIT":
      return {
        ...state,
        groupMemberStatus: "loading",
      };
    case "REQUEST_ALL_GROUPS_MEMBER_SUCCEED": {
      const sortedMemberGroups: Group[] = action.payload.groups.sort(
        (a: Group, b: Group) => a.DisplayName.localeCompare(b.DisplayName)
      );

      const groups: Group[] = state.groups
        ? [...(state.groups as Group[])]
        : [];
      sortedMemberGroups.forEach((group) => {
        const existingGroup = groups.find((g) => g.Id === group.Id);
        if (!existingGroup) {
          groups.push(group);
        } else {
          existingGroup.IsMember = true;
        }
      });
      groups.forEach((group) => {
        // it's possible user lost group membership since last fetch
        if (!sortedMemberGroups.find((g) => g.Id === group.Id)) {
          group.IsMember = false;
        }
      });

      const sortedGroups =
        groups?.sort((a: Group, b: Group) =>
          a.DisplayName.localeCompare(b.DisplayName)
        ) ?? [];

      return {
        ...state,
        groupMemberStatus: "fulfilled",
        groupsMemberInit: true,
        groupsMember: sortedMemberGroups,
        groups: sortedGroups,
        workgroupFilters: createWorkgroupFilter(sortedGroups),
      };
    }
    case "REQUEST_ALL_GROUPS_MEMBER_FAILED":
      return {
        ...state,
        groupMemberStatus: "failed",
        groupsMemberInit: true,
        groupsMember: undefined,
      };
    case "REQUEST_ALL_GROUPS_HASPROMPTS_INIT":
      return {
        ...state,
        groupHasPromptsStatus: "loading",
      };
    case "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED": {
      const sortedHasPromptsGroups: Group[] = action.payload.groups.sort(
        (a: Group, b: Group) => a.DisplayName.localeCompare(b.DisplayName)
      );

      const groups: Group[] = state.groups
        ? [...(state.groups as Group[])]
        : [];
      sortedHasPromptsGroups.forEach((group) => {
        const existingGroup = groups.find((g) => g.Id === group.Id);
        if (!existingGroup) {
          groups.push(group);
        } else {
          existingGroup.HasPrompts = true;
        }
      });

      const sortedGroups =
        groups?.sort((a: Group, b: Group) =>
          a.DisplayName.localeCompare(b.DisplayName)
        ) ?? [];

      return {
        ...state,
        groupHasPromptsStatus: "fulfilled",
        groupsHasPromptsInit: true,
        groupsHasPrompts: sortedHasPromptsGroups,
        groups: sortedGroups,
        workgroupFilters: createWorkgroupFilter(sortedGroups),
      };
    }
    case "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED":
      return {
        ...state,
        groupHasPromptsStatus: "failed",
        groupsHasPromptsInit: true,
        groupsHasPrompts: undefined,
      };
    default:
      return state;
  }
};

export type UseAllPromptsLibraryReducerProps = {
  serviceHost: string;
  locale: string;
};

function useAllPromptsLibrary({
  serviceHost,
  locale,
}: UseAllPromptsLibraryReducerProps) {
  const [searchParams] = useSearchParams();
  const initialStatePopulated = React.useMemo(() => {
    const isTeamsTab = localStorage.getItem("IsTeamsTab");
    let selectedTab = initialState.selectedTab;
    if (isTeamsTab === "true") {
      selectedTab = "GroupPrompts";
    }
    return {
      ...initialState,
      selectedTab: selectedTab,
      initSearchParams: searchParams,
      serviceHost,
      locale,
    };
  }, [locale, searchParams, serviceHost]);

  const [state, dispatch] = useReducer(reducer, initialStatePopulated);

  return {
    state,
    dispatch,
  };
}

export default useAllPromptsLibrary;
